import React from 'react';
import { Badge } from 'primereact/badge';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

interface PartialOption {
  storesInvolved: number;
  totalAvailableAcrossStores: number;
  storeAssignments: Array<{
    storeCode: string;
    storeName: string;
    totalQuantityAssigned: number;
    assignedItems: Array<{
      lineItemId: string;
      productName: string;
      productSku: string;
      quantityAssigned: number;
      quantityAvailable: number;
    }>;
  }>;
}

interface PartialFulfillmentOptionsProps {
  partialOptions: PartialOption[];
  selectedPartialOption: number | null;
  onPartialSelect: (index: number) => void;
}

interface TableRow {
  storeCode: string;
  storeName: string;
  productName: string;
  productSku: string;
  quantityAssigned: number;
  quantityAvailable: number;
  isFirstRowForStore: boolean;
  storeRowSpan: number;
}

export const PartialFulfillmentOptions: React.FC<PartialFulfillmentOptionsProps> = ({
  partialOptions,
  selectedPartialOption,
  onPartialSelect
}) => {
  if (partialOptions.length === 0) {
    return (
      <div className="text-center p-6 border-1 surface-border border-round bg-blue-50">
        <div className="mb-4">
          <i className="pi pi-info-circle" style={{ fontSize: '3rem', color: 'var(--blue-500)' }}></i>
        </div>
        <h5 className="text-blue-900 mb-3 font-semibold">No Partial Options Found</h5>
        <p className="text-blue-700 mb-0 line-height-3">
          No partial fulfillment options available. Please select a complete fulfillment option or assign items individually.
        </p>
      </div>
    );
  }

  const prepareTableData = (option: PartialOption): TableRow[] => {
    const tableData: TableRow[] = [];
    
    option.storeAssignments.forEach((assignment) => {
      assignment.assignedItems.forEach((item, itemIndex) => {
        tableData.push({
          storeCode: assignment.storeCode,
          storeName: assignment.storeName,
          productName: item.productName,
          productSku: item.productSku,
          quantityAssigned: item.quantityAssigned,
          quantityAvailable: item.quantityAvailable,
          isFirstRowForStore: itemIndex === 0,
          storeRowSpan: assignment.assignedItems.length
        });
      });
    });
    
    return tableData;
  };

  const storeNameTemplate = (rowData: TableRow) => {
    return (
      <div className="font-semibold text-primary">
        {rowData.storeName}
      </div>
    );
  };

  const productNameTemplate = (rowData: TableRow) => {
    return (
      <div>
        <div className="font-medium">{rowData.productName}</div>
        <div className="text-xs text-color-secondary mt-1">SKU: {rowData.productSku}</div>
      </div>
    );
  };

  const quantityTemplate = (rowData: TableRow) => {
    return (
      <div className="text-center">
        <span className="font-semibold">{rowData.quantityAssigned}</span>
      </div>
    );
  };

  const availableTemplate = (rowData: TableRow) => {
    return (
      <div className="text-center">
        <span className="text-color-secondary">{rowData.quantityAvailable}</span>
      </div>
    );
  };

  const handleAccordionChange = (optionIndex: number) => {
    onPartialSelect(optionIndex);
  };
  
  return (
    <div>
      <h6 className="mb-3 flex align-items-center gap-2">
        <i className="pi pi-sitemap text-blue-600" style={{ marginRight: '5px' }}></i>
        <span>Partial Fulfillment Options</span>
        <Badge value={partialOptions.length} severity="info" style={{ marginLeft: '8px' }} />
      </h6>
      
      <div className="inventory-list-dark">
        <ul className="inventory-list-group-dark">
          {partialOptions.map((option, index) => (
            <li 
              key={index} 
              className={`inventory-list-item-dark cursor-pointer ${
                selectedPartialOption === index ? 'active' : ''
              }`}
              onClick={() => onPartialSelect(index)}
            >
              <div className="d-flex justify-content-between align-items-center w-100">
                <div className="flex-1">
                  <div className="font-semibold">
                    {option.storeAssignments.map(assignment => assignment.storeName).join(' & ')}
                  </div>
                  <div className="text-sm text-color-secondary mt-1">
                    Total available: {option.totalAvailableAcrossStores} units across {option.storeAssignments.length} location{option.storeAssignments.length > 1 ? 's' : ''}
                  </div>
                </div>
                <div className="flex align-items-center gap-2">
                  <Badge 
                    value={`${option.storeAssignments.reduce((sum, store) => sum + store.totalQuantityAssigned, 0)} items`} 
                    severity="info" 
                  />
                  {selectedPartialOption === index && (
                    <i className="pi pi-check-circle text-green-600"></i>
                  )}
                </div>
              </div>
              
              <div className="mt-3">
                <Accordion 
                  className="inventory-accordion"
                  activeIndex={selectedPartialOption === index ? 0 : null}
                  onTabChange={(e) => {
                    if (e.index === 0) {
                      handleAccordionChange(index);
                    }
                  }}
                >
                  <AccordionTab header={`View Store Assignment Details (${option.storeAssignments.length} stores)`}>
                    <div className="pt-2">
                      <DataTable 
                        value={prepareTableData(option)} 
                        size="small"
                        stripedRows
                        className="p-datatable-sm"
                        showGridlines
                      >
                        <Column 
                          field="storeName" 
                          header="Store" 
                          body={storeNameTemplate}
                          style={{ width: '25%' }}
                        />
                        <Column 
                          field="productName" 
                          header="Product" 
                          body={productNameTemplate}
                          style={{ width: '45%' }}
                        />
                        <Column 
                          field="quantityAssigned" 
                          header="Assigned" 
                          body={quantityTemplate}
                          style={{ width: '15%' }}
                        />
                        <Column 
                          field="quantityAvailable" 
                          header="Available" 
                          body={availableTemplate}
                          style={{ width: '15%' }}
                        />
                      </DataTable>
                    </div>
                  </AccordionTab>
                </Accordion>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {selectedPartialOption !== null && (
        <div className="mt-4 p-3 border-1 surface-border border-round bg-blue-50">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-info-circle text-blue-600"></i>
            <span className="text-blue-900">
              Ready to assign items across {partialOptions[selectedPartialOption].storesInvolved} stores
            </span>
          </div>
        </div>
      )}
    </div>
  );
}; 